import { O } from 'ts-toolbelt'
import { If } from 'ts-toolbelt/out/Any/If'

import {
  DeliveryPreorderWindow,
  FulfilmentMethodASAPOrderability,
  FulfilmentMethodOrderability,
  FulfilmentMethodPreorderOrderability,
  OrderabilityOutput,
  Outlet,
  Scalars,
} from '@src/graphql-types'

import {
  ParsedCollectionPreorderTime,
  ParsedDeliveryPreorderWindow,
} from './types'
import { cloneDeep, get, set } from 'lodash'
import { isValid } from 'date-fns'

// Util to replace string with date, removing undefined, while retaining null
// This is used where we have asserted that an object has a field (ie is not possibly undefined)
// but the server response may still be null, so we will have parsed the date if it is not null
type ReplaceOptionalStringWithRequiredDate<
  T extends string | null | undefined
> = Exclude<T, string | undefined> | Date
/**
 *
 * Parses for fields which are dates but will be returned as strings from the API
 */
// collection preorder time
const parseCollectionPreorderTime = (
  collectionPreorderTime: Scalars['DateTime']
): ParsedCollectionPreorderTime => new Date(collectionPreorderTime)
// collection preorder time array
const parseCollectionPreorderTimes = (
  collectionPreorderTimes: Scalars['DateTime'][]
): ParsedCollectionPreorderTime[] =>
  collectionPreorderTimes.map(parseCollectionPreorderTime)
// delivery preorder window
const parseDeliveryPreorderWindow = (
  deliveryPreorderWindow: DeliveryPreorderWindow
): ParsedDeliveryPreorderWindow => ({
  ...deliveryPreorderWindow,
  start: new Date(deliveryPreorderWindow.start),
  end: new Date(deliveryPreorderWindow.end),
})
// delivery preorder window array
const parseDeliveryPreorderWindows = (
  deliveryPreorderWindows: DeliveryPreorderWindow[]
): ParsedDeliveryPreorderWindow[] =>
  deliveryPreorderWindows.map(parseDeliveryPreorderWindow)

// FULL OUTLET
// outlet date fields as dates
// ts util to conditionally overwrite date-string fields and nested orderability on T
export type DateifyOutlet<T extends O.Partial<Outlet, 'deep'>> = O.Overwrite<
  T,
  {
    closeDate: If<O.Has<T, 'closeDate'>, Date | null>
    nextOpenDate: If<O.Has<T, 'nextOpenDate'>, Date>
    closedUntil: If<O.Has<T, 'closedUntil'>, Date | null | undefined>
    deliveryPreorderWindows: If<
      O.Has<T, 'deliveryPreorderWindows'>,
      ParsedDeliveryPreorderWindow[]
    >
    collectionPreorderTimes: If<
      O.Has<T, 'collectionPreorderTimes'>,
      ParsedCollectionPreorderTime[]
    >
    orderability: If<
      O.Has<T, 'orderability'>,
      DateifyOrderabilityOutput<Exclude<T['orderability'], null | undefined>>
    >
  }
>
// Takes an outlet-like object and returns it with date fields parsed
// If `orderabilty` is included, then this is also parsed
// Returned types use inference to ensure that return types only
// contain fields that were present on the input, but with the parsed Date types
export const parseOutletDates = <
  T extends O.Partial<
    Pick<
      Outlet,
      | 'closeDate'
      | 'nextOpenDate'
      | 'closedUntil'
      | 'deliveryPreorderWindows'
      | 'collectionPreorderTimes'
      | 'orderability'
    >,
    'deep'
  >
>({
  closeDate,
  nextOpenDate,
  closedUntil,
  deliveryPreorderWindows,
  collectionPreorderTimes,
  orderability,
  ...otherOutletFields
}: T): DateifyOutlet<T> =>
  ({
    ...otherOutletFields,
    ...(closeDate !== undefined && {
      closeDate: closeDate ? new Date(closeDate) : null,
    }),
    ...(nextOpenDate !== undefined && {
      nextOpenDate: new Date(nextOpenDate),
    }),
    ...(closedUntil !== undefined && {
      closedUntil: closedUntil ? new Date(closedUntil) : null,
    }),
    ...(deliveryPreorderWindows && {
      deliveryPreorderWindows: parseDeliveryPreorderWindows(
        deliveryPreorderWindows as unknown as DeliveryPreorderWindow[]
      ),
    }),
    ...(collectionPreorderTimes && {
      collectionPreorderTimes: parseCollectionPreorderTimes(
        collectionPreorderTimes as unknown as Scalars['DateTime'][]
      ),
    }),
    ...(orderability !== undefined && {
      orderability: orderability
        ? (parseOrderabilityDates(orderability) as DateifyOrderabilityOutput<
            Exclude<T['orderability'], undefined>
          >)
        : null,
    }),
  } as DateifyOutlet<T>)

/** ORDERABILITY OUTPUT
 * Conditionally overwrites the properties that actually exist on T with the dateified versions
 * this allows a partial object of OrderabilityOutput to be passed in
 * and the return type won't contain any dateified fields that don't exist on the input type
 **/
export type DateifyOrderabilityOutput<
  T extends O.Partial<OrderabilityOutput, 'deep'>
> = O.Overwrite<
  // overwrite the properties of T with dateified versions
  T,
  {
    // if T has the key 'delivery', overwrite it with the dateified version
    // (uses Exclude to remove the undefined as O.Has already asserts it's on the object)
    delivery: If<
      O.Has<T, 'delivery'>,
      DateifyFulfilmentMethodOrderability<Exclude<T['delivery'], undefined>>
    >
    collection: If<
      O.Has<T, 'collection'>,
      DateifyFulfilmentMethodOrderability<Exclude<T['collection'], undefined>>
    >
    table: If<
      O.Has<T, 'table'>,
      DateifyFulfilmentMethodOrderability<Exclude<T['table'], undefined>>
    >
    anyFulfilmentMethod: If<
      O.Has<T, 'anyFulfilmentMethod'>,
      DateifyFulfilmentMethodOrderability<
        Exclude<T['anyFulfilmentMethod'], undefined>
      >
    >
  }
>
// same as above but for the next level down
type DateifyFulfilmentMethodOrderability<
  T extends O.Partial<FulfilmentMethodOrderability, 'deep'>
> = O.Overwrite<
  T,
  {
    asap: If<
      O.Has<T, 'asap'>,
      DateifyFulfilmentMethodASAPOrderability<Exclude<T['asap'], undefined>>
    >
    preorder: If<
      O.Has<T, 'preorder'>,
      DateifyFulfilmentMethodPreorderOrderability<
        Exclude<T['preorder'], undefined>
      >
    >
  }
>
// and again, but this time we're actually at the fields which will be parsed to dates
type DateifyFulfilmentMethodASAPOrderability<
  T extends O.Partial<FulfilmentMethodASAPOrderability, 'deep'>
> = O.Overwrite<
  T,
  {
    availableFrom: If<
      O.Has<T, 'availableFrom'>,
      ReplaceOptionalStringWithRequiredDate<T['availableFrom']>
    >
    validUntil: If<
      O.Has<T, 'validUntil'>,
      ReplaceOptionalStringWithRequiredDate<T['validUntil']>
    >
  }
>
type DateifyFulfilmentMethodPreorderOrderability<
  T extends O.Partial<FulfilmentMethodPreorderOrderability, 'deep'>
> = O.Overwrite<
  T,
  {
    nextPreorderFulfilledAt: If<
      O.Has<T, 'nextPreorderFulfilledAt'>,
      ReplaceOptionalStringWithRequiredDate<T['nextPreorderFulfilledAt']>
    >
  }
>

const datePaths = [
  'delivery.asap.availableFrom',
  'delivery.asap.validUntil',
  'delivery.preorder.nextPreorderFulfilledAt',
  'collection.asap.availableFrom',
  'collection.asap.validUntil',
  'collection.preorder.nextPreorderFulfilledAt',
  'table.asap.availableFrom',
  'table.asap.validUntil',
  'table.preorder.nextPreorderFulfilledAt',
  'anyFulfilmentMethod.asap.availableFrom',
  'anyFulfilmentMethod.asap.validUntil',
  'anyFulfilmentMethod.preorder.nextPreorderFulfilledAt',
]
// get each known date path in orderability and set it to a Date if it parses correctly
function parseOrderabilityDates<
  T extends O.Partial<OrderabilityOutput, 'deep'>
>(orderability: T): DateifyOrderabilityOutput<T> {
  const clonedOrderability = cloneDeep(orderability)
  for (const path of datePaths) {
    const value = get(clonedOrderability, path)
    if (value && typeof value === 'string') {
      const dateValue = new Date(value)
      set(clonedOrderability, path, isValid(dateValue) ? dateValue : null)
    }
  }
  return clonedOrderability as unknown as DateifyOrderabilityOutput<T>
}
